import { supabase } from '@/integrations/supabase/client';
import { CopilotThread } from '../../types/database-types';
import { logInfo, logError, LogCategory, startMeasure, endMeasure } from '@/utils/logger';
import { OpenAIThreadsService } from '../openai/threads.service';

/**
 * Service for thread-related database operations
 * 
 * THREAD ARCHITECTURE:
 * - Database threads (agent_threads) store conversation metadata
 * - OpenAI threads handle the actual AI conversation state
 * - Each database thread has an openai_thread_id field linking them
 * - Frontend only needs database thread IDs
 * - Edge functions lookup OpenAI thread IDs from database when needed
 * 
 * THREAD CREATION:
 * - Use createThreadWithOpenAI() for new AI conversations (recommended)
 * - Creates both database thread and OpenAI thread, links them
 * - Regular createThread() only creates database thread (incomplete for AI chat)
 * 
 * OPENAI THREAD ID STORAGE:
 * - Stored ONLY in database field: agent_threads.openai_thread_id
 * - NOT stored in metadata to avoid dual storage confusion
 * - Edge functions retrieve via database lookup: SELECT openai_thread_id WHERE id = ?
 */
export class ThreadsService {
  /**
   * Fetch all threads for a user with assistants
   * SCALABLE: Filters by both owned threads AND editor-accessible threads upfront
   */
  static async fetchThreads(userId: string): Promise<CopilotThread[]> {
    try {
      logInfo('Fetching threads for user', { 
        category: LogCategory.DATABASE,
        data: { userId }
      });

      // STEP 1: Get workbook IDs where user is an editor (first query)
      const { data: editorWorkbooks, error: editorWorkbookError } = await supabase
        .from('workbook_editors')
        .select('workbook_id')
        .eq('editor_id', userId);

      if (editorWorkbookError) {
        logError('Failed to fetch editor workbooks', {
          category: LogCategory.DATABASE,
          data: { error: editorWorkbookError.message, userId }
        });
      }

      const editorWorkbookIds = editorWorkbooks?.map(row => row.workbook_id) || [];

      // STEP 2: Get agent thread IDs for those workbooks (second query, only if needed)
      let accessibleThreadIds: string[] = [];
      
      if (editorWorkbookIds.length > 0) {
        const { data: editorThreadIds, error: editorThreadError } = await supabase
          .from('planner_goals_workbooks')
          .select('agent_thread_id')
          .in('workbook_id', editorWorkbookIds)
          .not('agent_thread_id', 'is', null);

        if (editorThreadError) {
          logError('Failed to fetch editor thread IDs', {
            category: LogCategory.DATABASE,
            data: { error: editorThreadError.message, userId }
          });
        } else {
          accessibleThreadIds = editorThreadIds?.map(row => row.agent_thread_id).filter(Boolean) || [];
        }
      }
      
      logInfo('Editor-accessible threads found', {
        category: LogCategory.DATABASE,
        data: { userId, editorThreadCount: accessibleThreadIds.length }
      });

      // STEP 3: Fetch threads with efficient OR filter (third query)
      // Gets threads where user is either owner OR has editor access
      let threadsQuery = supabase
        .from('agent_threads')
        .select(`
          *,
          assistants:agent_thread_assistants(
            assistant:agent_assistants(*)
          )
        `)
        .eq('is_archived', false)
        .order('updated_at', { ascending: false });

      // Apply efficient filtering: owned threads OR editor-accessible threads
      if (accessibleThreadIds.length > 0) {
        // User has editor access to some threads
        threadsQuery = threadsQuery.or(`user_id.eq.${userId},id.in.(${accessibleThreadIds.join(',')})`);
      } else {
        // User only has owned threads
        threadsQuery = threadsQuery.eq('user_id', userId);
      }

      const { data, error } = await threadsQuery;

      if (error) {
        throw new Error(`Failed to fetch threads: ${error.message}`);
      }

      // Transform the data to match our expected format
      const threads = data?.map(thread => ({
        ...thread,
        userId: thread.user_id,
        lastMsgFromUser: thread.last_msg_from_user || '',
        isPinned: thread.is_pinned || false,
        isArchived: thread.is_archived || false,
        createdAt: thread.created_at,
        updatedAt: thread.updated_at,
        openaiThreadId: thread.openai_thread_id || undefined,
        metadata: (thread.metadata as Record<string, any>) || {},
        assistants: thread.assistants?.map((ta: any) => ta.assistant).filter(Boolean) || []
      })) || [];

      logInfo('Threads fetched successfully', { 
        category: LogCategory.DATABASE,
        data: { 
          userId, 
          totalCount: threads.length,
          ownedCount: threads.filter(t => t.userId === userId).length,
          editorAccessCount: threads.filter(t => t.userId !== userId).length
        }
      });

      return threads;
    } catch (error: any) {
      logError('Failed to fetch threads', {
        category: LogCategory.DATABASE,
        data: { error: error.message, userId }
      });
      throw error;
    }
  }

  /**
   * Create a new thread
   * 
   * ⚠️  WARNING: This method only creates a database thread without OpenAI integration.
   * For AI chat functionality, use createThreadWithOpenAI() instead.
   * 
   * @deprecated Use createThreadWithOpenAI() for complete thread setup with AI integration
   */
  static async createThread(threadData: {
    userId: string;
    title: string;
    lastMsgFromUser?: string;
    isPinned?: boolean;
    isArchived?: boolean;
    metadata?: any;
    openaiThreadId?: string; // Optional OpenAI thread ID to include during creation
  }): Promise<CopilotThread> {
    try {
      logInfo('Creating new thread', { 
        category: LogCategory.DATABASE,
        data: { userId: threadData.userId, title: threadData.title, hasOpenAIId: !!threadData.openaiThreadId }
      });

      const insertData: any = {
        user_id: threadData.userId,
        title: threadData.title,
        last_msg_from_user: threadData.lastMsgFromUser || '',
        is_pinned: threadData.isPinned || false,
        is_archived: threadData.isArchived || false,
        metadata: threadData.metadata || {}
      };

      // Include OpenAI thread ID if provided
      if (threadData.openaiThreadId) {
        insertData.openai_thread_id = threadData.openaiThreadId;
      }

      const { data, error } = await supabase
        .from('agent_threads')
        .insert(insertData)
        .select(`
          *,
          assistants:agent_thread_assistants(
            assistant:agent_assistants(*)
          )
        `)
        .single();

      if (error) {
        throw new Error(`Failed to create thread: ${error.message}`);
      }

      const thread: CopilotThread = {
        ...data,
        userId: data.user_id,
        lastMsgFromUser: data.last_msg_from_user || '',
        isPinned: data.is_pinned || false,
        isArchived: data.is_archived || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        openaiThreadId: data.openai_thread_id || undefined,
        metadata: (data.metadata as Record<string, any>) || {},
        assistants: data.assistants?.map((ta: any) => ta.assistant).filter(Boolean) || []
      };

      logInfo('Thread created successfully', { 
        category: LogCategory.DATABASE,
        data: { threadId: thread.id, openaiThreadId: thread.openaiThreadId }
      });

      return thread;
    } catch (error: any) {
      logError('Failed to create thread', {
        category: LogCategory.DATABASE,
        data: { error: error.message }
      });
      throw error;
    }
  }

  /**
   * Update the last user message in a thread
   */
  static async updateLastUserMessage(threadId: string, lastMessage: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('agent_threads')
        .update({
          last_msg_from_user: lastMessage,
          updated_at: new Date().toISOString()
        })
        .eq('id', threadId);

      if (error) {
        throw new Error(`Failed to update thread: ${error.message}`);
      }

      logInfo('Thread last message updated', { 
        category: LogCategory.DATABASE,
        data: { threadId }
      });
    } catch (error: any) {
      logError('Failed to update thread last message', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId }
      });
      throw error;
    }
  }

  /**
   * Add an assistant to a thread
   */
  static async addAssistantToThread(threadId: string, assistantId: string): Promise<void> {
    try {
      // First check if the assistant is already associated with the thread
      const { data: existingAssociation, error: checkError } = await supabase
        .from('agent_thread_assistants')
        .select('id')
        .eq('thread_id', threadId)
        .eq('assistant_id', assistantId)
        .maybeSingle();

      if (checkError) {
        throw new Error(`Failed to check assistant association: ${checkError.message}`);
      }

      // If association already exists, no need to insert
      if (existingAssociation) {
        logInfo('Assistant already associated with thread', { 
          category: LogCategory.DATABASE,
          data: { threadId, assistantId }
        });
        return;
      }

      // Insert the new association since it doesn't exist
      const { error: insertError } = await supabase
        .from('agent_thread_assistants')
        .insert({
          thread_id: threadId,
          assistant_id: assistantId
        });

      if (insertError) {
        throw new Error(`Failed to add assistant to thread: ${insertError.message}`);
      }

      // Update the thread's updated_at timestamp
      await supabase
        .from('agent_threads')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', threadId);

      logInfo('Assistant added to thread', { 
        category: LogCategory.DATABASE,
        data: { threadId, assistantId }
      });
    } catch (error: any) {
      logError('Failed to add assistant to thread', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId, assistantId }
      });
      throw error;
    }
  }

  /**
   * Delete a thread
   */
  static async deleteThread(threadId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('agent_threads')
        .delete()
        .eq('id', threadId);

      if (error) {
        throw new Error(`Failed to delete thread: ${error.message}`);
      }

      logInfo('Thread deleted successfully', { 
        category: LogCategory.DATABASE,
        data: { threadId }
      });
    } catch (error: any) {
      logError('Failed to delete thread', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId }
      });
      throw error;
    }
  }

  /**
   * Update thread with OpenAI thread ID
   */
  static async updateThreadWithOpenAIId(threadId: string, openaiThreadId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('agent_threads')
        .update({
          openai_thread_id: openaiThreadId,
          updated_at: new Date().toISOString()
        })
        .eq('id', threadId);

      if (error) {
        throw new Error(`Failed to update thread with OpenAI ID: ${error.message}`);
      }

      logInfo('Thread updated with OpenAI ID', { 
        category: LogCategory.DATABASE,
        data: { threadId, openaiThreadId }
      });
    } catch (error: any) {
      logError('Failed to update thread with OpenAI ID', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId }
      });
      throw error;
    }
  }

  /**
   * Create a new thread with both database and OpenAI thread
   * This is the consolidated method that ensures both threads are created and linked
   * @param executeAIOperation - The wrapper function from useAIOperationWrapper for credit checking
   */
  static async createThreadWithOpenAI(
    threadData: {
      userId: string;
      title: string;
      lastMsgFromUser?: string;
      isPinned?: boolean;
      isArchived?: boolean;
      metadata?: any;
    },
    executeAIOperation?: (operation: () => Promise<any>, options?: any) => Promise<any>
  ): Promise<CopilotThread> {
    const startTime = startMeasure('createThreadWithOpenAI');
    
    logInfo('Creating thread with OpenAI integration', { 
      category: LogCategory.DATABASE,
      data: { userId: threadData.userId, title: threadData.title }
    });

    try {
      // Step 1: Create OpenAI thread first with credit checking
      logInfo('Creating OpenAI thread first', { 
        category: LogCategory.DATABASE,
        data: { userId: threadData.userId }
      });

      const openaiThreadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      
      logInfo('OpenAI thread created, now creating database thread', { 
        category: LogCategory.DATABASE,
        data: { openaiThreadId }
      });

      // Step 2: Create database thread with OpenAI thread ID included
      const dbThread = await this.createThread({
        ...threadData,
        openaiThreadId: openaiThreadId
      });
      
      logInfo('Thread with OpenAI integration created successfully', { 
        category: LogCategory.DATABASE,
        data: { threadId: dbThread.id, openaiThreadId }
      });

      endMeasure(startTime, 'Thread with OpenAI creation complete', LogCategory.DATABASE);
      return dbThread;

    } catch (error: any) {
      const errorMsg = `Failed to create thread with OpenAI: ${error.message}`;
      logError('Thread with OpenAI creation failed', {
        category: LogCategory.DATABASE,
        data: { error: errorMsg, userId: threadData.userId }
      });
      endMeasure(startTime, 'Thread with OpenAI creation failed', LogCategory.DATABASE);
      throw new Error(errorMsg);
    }
  }
}
